import React, { useEffect, useMemo, useState } from 'react';
import { 
  VideoCameraIcon, 
  PlusIcon, 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon, 
  CalendarIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { ScheduledMeeting } from '../types';
import { useUsers } from '../hooks/useUsers';
import MeetingActivityMeta from './MeetingActivityMeta';
import MeetingDetailsTabs from './MeetingDetailsTabs';

interface ZoomMeetingsSectionProps {
  meetings: ScheduledMeeting[];
  onCreateMeeting: () => void;
  onJoinMeeting: (meeting: ScheduledMeeting) => void;
  onStartMeeting: (meeting: ScheduledMeeting) => void;
  onEndMeeting?: (meeting: ScheduledMeeting) => void;
  onEditMeeting: (meeting: ScheduledMeeting) => void;
  onDeleteMeeting: (meeting: ScheduledMeeting) => void;
  isAdmin: boolean;
  isCreator: boolean;
  currentUserId?: string;
  loading?: boolean;
}

export const ZoomMeetingsSection: React.FC<ZoomMeetingsSectionProps> = ({
  meetings,
  onCreateMeeting,
  onJoinMeeting,
  onStartMeeting,
  onEndMeeting,
  onEditMeeting,
  onDeleteMeeting,
  isAdmin,
  isCreator,
  currentUserId,
  loading = false
}) => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'ongoing' | 'past'>('upcoming');
  const { fetchUsers, getUserDisplayName } = useUsers();

  // Ensure host user profiles are loaded for display
  useEffect(() => {
    const hostIds = Array.from(new Set((meetings || []).map((m) => m.hostId).filter(Boolean)));
    if (hostIds.length > 0) {
      fetchUsers(hostIds);
    }
  }, [meetings, fetchUsers]);

  // Filter meetings by status
  const now = new Date();
  const upcomingMeetings = meetings.filter(meeting => {
    const startTime = new Date(meeting.startTime);
    return meeting.status !== 'cancelled' && meeting.status !== 'finished' && startTime > now;
  });

  const ongoingMeetings = meetings.filter(meeting => {
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(startTime.getTime() + (meeting.duration || 60) * 60000);
    return meeting.status !== 'cancelled' && meeting.status !== 'finished' && now >= startTime && now <= endTime;
  });

  const pastMeetings = meetings.filter(meeting => {
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(startTime.getTime() + (meeting.duration || 60) * 60000);
    return meeting.status === 'finished' || endTime < now;
  });

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Africa/Accra'
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      timeZone: 'Africa/Accra'
    });
  };

  const getMeetingStatus = (meeting: ScheduledMeeting) => {
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(startTime.getTime() + (meeting.duration || 60) * 60000);
    
    if (meeting.status === 'cancelled') return 'cancelled';
    if (meeting.status === 'finished') return 'finished';
    if (now >= startTime && now <= endTime) return 'ongoing';
    if (now < startTime) return 'upcoming';
    return 'past';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing': return 'bg-red-100 text-red-800 border-red-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'past': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'finished': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ongoing': return 'LIVE NOW';
      case 'upcoming': return 'Upcoming';
      case 'past': return 'Past';
      case 'cancelled': return 'Cancelled';
      case 'finished': return 'Finished';
      default: return 'Unknown';
    }
  };

  const renderMeetingCard = (meeting: ScheduledMeeting) => {
    const status = getMeetingStatus(meeting);
    const isHost = currentUserId && meeting.hostId === currentUserId;

    return (
      <div key={meeting.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 text-lg mb-1 line-clamp-2">
              {meeting.topic || 'Untitled Meeting'}
            </h4>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                {formatDate(meeting.startTime)}
              </div>
              <div className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1" />
                {formatTime(meeting.startTime)} • {meeting.duration || 60}min
              </div>
            </div>
            
            {/* Show meeting ID and password only to the host */}
            {isHost && (
              <div className="mt-2 space-y-1">
                {meeting.id && (
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="font-medium mr-2">Meeting ID:</span>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">{meeting.id}</span>
                  </div>
                )}
                {meeting.password && (
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="font-medium mr-2">Password:</span>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">{meeting.password}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(status)}`}>
            {getStatusText(status)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-600">
            <UserGroupIcon className="h-4 w-4 mr-1" />
            <span>
              Host: {currentUserId && meeting.hostId === currentUserId
                ? 'You'
                : getUserDisplayName(meeting.hostId)}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {status === 'ongoing' && (
              <>
                {/* Host sees "Start Live", others see "Join Live" */}
                {isHost ? (
                  <button
                    onClick={() => onStartMeeting(meeting)}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PlayIcon className="h-3 w-3 mr-1" />
                    Start Live
                  </button>
                ) : (
                  <button
                    onClick={() => onJoinMeeting(meeting)}
                    className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <PlayIcon className="h-3 w-3 mr-1" />
                    Join Live
                  </button>
                )}
              </>
            )}

            {status === 'ongoing' && (isAdmin || isHost) && (
              <button
                onClick={() => onEndMeeting && onEndMeeting(meeting)}
                className="inline-flex items-center px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
                title="End meeting"
              >
                End
              </button>
            )}
            
            {status === 'upcoming' && (
              <>
                {isHost && (
                  <button
                    onClick={() => onStartMeeting(meeting)}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PlayIcon className="h-3 w-3 mr-1" />
                    Start
                  </button>
                )}
                <button
                  onClick={() => onJoinMeeting(meeting)}
                  className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  <EyeIcon className="h-3 w-3 mr-1" />
                  Join
                </button>
              </>
            )}

            {(isAdmin || isHost) && (
              <>
                <button
                  onClick={() => onEditMeeting(meeting)}
                  className="inline-flex items-center px-2 py-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                  title="Edit meeting"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDeleteMeeting(meeting)}
                  className="inline-flex items-center px-2 py-1.5 text-gray-600 hover:text-red-600 transition-colors"
                  title="Delete meeting"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {status === 'past' ? (
          <MeetingDetailsTabs meetingId={meeting.id} />
        ) : (
          <MeetingActivityMeta meetingId={meeting.id} className="mt-3" />
        )}
      </div>
    );
  };

  const renderEmptyState = (type: string) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
        <VideoCameraIcon className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No {type} meetings
      </h3>
      <p className="text-gray-500 mb-4">
        {type === 'upcoming' 
          ? 'Schedule your first meeting to get started with live sessions.'
          : type === 'ongoing'
          ? 'No meetings are currently in progress.'
          : 'No past meetings to display.'}
      </p>
      {type === 'upcoming' && isCreator && (
        <button
          onClick={onCreateMeeting}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Meeting
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <VideoCameraIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Zoom Meetings</h3>
          </div>
          {isCreator && (
            <button
              onClick={onCreateMeeting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Meeting
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'upcoming'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upcoming ({upcomingMeetings.length})
          </button>
          <button
            onClick={() => setActiveTab('ongoing')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'ongoing'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Live Now ({ongoingMeetings.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'past'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Past ({pastMeetings.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'upcoming' && (
          <div className="space-y-4">
            {upcomingMeetings.length > 0 ? (
              upcomingMeetings.map(renderMeetingCard)
            ) : (
              renderEmptyState('upcoming')
            )}
          </div>
        )}

        {activeTab === 'ongoing' && (
          <div className="space-y-4">
            {ongoingMeetings.length > 0 ? (
              ongoingMeetings.map(renderMeetingCard)
            ) : (
              renderEmptyState('ongoing')
            )}
          </div>
        )}

        {activeTab === 'past' && (
          <div className="space-y-4">
            {pastMeetings.length > 0 ? (
              pastMeetings.map(renderMeetingCard)
            ) : (
              renderEmptyState('past')
            )}
          </div>
        )}
      </div>
    </div>
  );
};
